import React, { FC } from 'react'

import { DevTool } from '@hookform/devtools'
import { FormProvider } from 'react-hook-form'

import { useCustomerSignUp } from 'Hooks/useCustomerSignUp'
import Arrow from 'Icons/arrow-right-circle.svg'
import { Customer } from 'Types'

import { Code } from './Code'
import { Identify } from './Identify'
import { LoggedCustomerView } from './LoggedCustomerView'
import { Phone } from './Phone'
import { ResendButton } from './ResendButton'
import { VerifiedBadge } from './VerifiedBadge'

interface Props {
  currentCustomer: Customer | null
  onLoginComplete(): void
}

const LoginForm: FC<Props> = ({ currentCustomer, onLoginComplete }) => {
  const {
    codeInputRef,
    form,
    handleLogout,
    handleResend,
    onSubmit,
    resendSecondsLeft,
    showDetails,
    signing,
    state,
    stopResendCooldown,
    twoFactorAuth,
  } = useCustomerSignUp(currentCustomer, { onSignedUp: onLoginComplete })

  const phoneSectionDescription = state.isCode
    ? 'Enter the 6-digit code we sent to this number'
    : 'This number is verified'

  return (
    <FormProvider {...form}>
      {/* Renders its own fixed-position overlay, so it stays outside the
          layout container rather than becoming a flex child of it. */}
      <DevTool control={form.control} />

      <div className="user-profile-login">
        <h2 className="user-profile-login__heading">Sign in</h2>

        <form
          className="user-profile-login__form"
          hidden={currentCustomer?.signedUp}
          onSubmit={(event) => void onSubmit(event)}
        >
          {state.isCode ? (
            <p aria-live="polite" className="sr-only" role="status">
              Enter the 6-digit verification code to continue.
            </p>
          ) : null}

          <div className="user-profile-login__flow">
            {currentCustomer?.signedUp ? null : (
              <section className="user-profile-login__panel">
                {twoFactorAuth && !state.isPhone ? (
                  <>
                    <div className="user-profile-login__title-row">
                      <h3 className="user-profile-login__title">Verify Phone</h3>
                      {state.isCode ? null : <VerifiedBadge />}
                    </div>
                    <p className="user-profile-login__description">{phoneSectionDescription}</p>
                  </>
                ) : null}
                <Phone disabled={!state.isPhone} submitting={signing} />
                {/* Stays mounted (like Phone/Details' fields) even once past the code
                    step — react-hook-form's useController force-unregisters a field
                    when its component unmounts regardless of shouldUnregister, so
                    conditionally unrendering Code here was dropping `code` from the
                    form entirely once the flow advanced, and the next step's submit
                    (e.g. password) went out without it. Visibility stays controlled
                    by the wrapper's `hidden` attribute only. */}
                <div aria-hidden={!state.isCode} className="user-profile-login__code-reveal" hidden={!state.isCode}>
                  <Code ref={codeInputRef} canAutoSubmit={!signing} onSubmit={() => void onSubmit()} />
                </div>
              </section>
            )}

            {showDetails || state.isPassword ? <Identify showDetails={showDetails} state={state} /> : null}
          </div>
        </form>

        {currentCustomer?.signedUp ? (
          <LoggedCustomerView
            email={currentCustomer.email}
            firstName={currentCustomer.firstName}
            lastName={currentCustomer.lastName}
          />
        ) : null}

        {!currentCustomer?.signedUp && !state.isPhone ? (
          <div className="user-profile-login__actions">
            <div className="user-profile-login__actions-secondary">
              {state.isCode ? (
                <ResendButton duration={resendSecondsLeft} onResend={handleResend} onStop={stopResendCooldown} />
              ) : null}

              <button className="user-profile-link-button" type="button" onClick={handleLogout}>
                Use a different number
              </button>
            </div>

            <button
              className="btn btn-accent user-profile-login__submit"
              disabled={signing}
              type="button"
              onClick={() => void onSubmit()}
            >
              <span>Continue</span>
              <Arrow className="sociavore-icon sociavore-icon--fill" />
            </button>
          </div>
        ) : null}
      </div>
    </FormProvider>
  )
}

export default LoginForm
